import React, { useEffect, useState, useContext } from 'react';
import { connect } from 'react-redux';
import { Form } from 'react-final-form';
import { composeValidators, noStartTrailWhitespace, required } from 'spa/components/form/validate';
import {
  kycGotoNextPage as kycGotoNextPageRoutine,
  kycGotoPrevPage as kycGotoPrevPageRoutine,
  kycSetDraftCompanyDetails,
  kycSubmitCustomerDetails as kycSubmitCustomerDetailsRoutine,
} from 'spa/actions/VerificationActions';
import { Alpha3ToAlpha2 } from 'spa/constants/ISOCountryCodes';
import VerificationConstants, { KYCPages } from 'spa/constants/VerificationConstants';
import ActionButtons from 'spa/components/CustomerVerification/V2/ActionButtons';
import {
  customerKycLoadingUiSelector,
  customerKycSubmitLoadingSelector,
  buyerCheckoutDetailsSelector,
  customerKyc3ApprovedSelector,
  customerCompanyNameSelector,
  customerKycHasPrevPagesSelector,
  customerSelector,
  customerKycDraftCompanyDetailsSelector,
} from 'spa/selectors/CustomerSelectors';
import { KYCContext } from 'spa/context/KYCContext';
import { isUboFlowEnabled } from 'spa/utils/Verification';
import Heading from './Heading';
import { InputField, AddressFieldSet, IndustryField } from './Fields';

const CompanyDetailsForm = (props) => {
  const {
    isNavigating,
    isSubmitting,
    loadPrevPage,
    gotoNextPage,
    setDraftCompanyDetails,
    submitIndividualDetails,
    companyDetails,
    draftCompanyDetails,
    isCompanyInformationVerified,
    hasPrevPages,
    hasCompanyName,
    industryOptions,
    industryId,
  } = props;
  // isSubmitting is used for the regular path
  // isNavigating is used for the UBO-shadowed path
  // This will eventually be the only flag on Phase 1 release
  const isBusy = isNavigating || isSubmitting;
  const [isEditting, setIsEditting] = useState(hasCompanyName);
  useEffect(() => {
    if (!isBusy) {
      setIsEditting(hasCompanyName);
    }
  }, [hasCompanyName, isBusy]);
  const kycContext = useContext(KYCContext);

  const defaultCountry =
    Alpha3ToAlpha2[window.config.geoip_country] &&
    Alpha3ToAlpha2[window.config.geoip_country].toLowerCase();
  const [showAutocompleteAddressError, setShowAutocompleteAddressError] = useState(false);
  const uboFlowEnabled = isUboFlowEnabled();
  const onFormSubmit = (values, form) => {
    if (uboFlowEnabled) {
      setDraftCompanyDetails({
        values,
        registeredFields: [...form.getRegisteredFields(), 'account-type'],
      });
      gotoNextPage({
        nextPage: KYCPages.HAS_UBO_FORM,
        forceNextPage: true,
        kycContext,
      });
      return;
    }
    submitIndividualDetails({
      values,
      form,
      formName: VerificationConstants.COMPANY_DETAILS_FORM,
      kycContext,
    });
  };
  return (
    <div>
      <Form
        onSubmit={onFormSubmit}
        subscription={{
          values: true,
          submitting: true,
          hasValidationErrors: true,
        }}
        initialValues={{
          'account-type': VerificationConstants.ACCOUNT_TYPE_COMPANY,
          industry_id: industryId || null,
          ...companyDetails,
          ...((draftCompanyDetails && draftCompanyDetails.values) || {}),
        }}
        mutators={{
          clearAutocompleteAddressError: ([prefix], state) => {
            state.fields[`${prefix}address`].data.error = undefined;
            state.fields[`${prefix}city`].data.error = undefined;
            state.fields[`${prefix}state`].data.error = undefined;
          },
        }}
      >
        {({ handleSubmit, hasValidationErrors, values }) => (
          <form onSubmit={handleSubmit} name={VerificationConstants.COMPANY_DETAILS_FORM}>
            <Heading text="Business Details" hasDivider />
            <InputField
              name="company-name"
              label="Company Name"
              validate={composeValidators([required, noStartTrailWhitespace])}
              disabled={isBusy || isCompanyInformationVerified}
            />
            <AddressFieldSet
              type="company"
              defaultCountry={companyDetails['company-country'] || defaultCountry || 'us'}
              showError={showAutocompleteAddressError}
              setShowError={setShowAutocompleteAddressError}
              disabled={isBusy}
            />
            {values['company-country'] === 'CA' && (
              <IndustryField isSubmitting={isBusy} industryOptions={industryOptions} />
            )}
            <ActionButtons
              secondaryText="Back"
              primaryText={uboFlowEnabled ? 'Next' : 'Save and Next'}
              isLoading={isBusy}
              isDisabled={hasValidationErrors}
              type="submit"
              withBack={hasPrevPages || isEditting}
              onSecondaryClick={() => loadPrevPage()}
              onPrimaryClick={() => {
                setShowAutocompleteAddressError(true);
              }}
            />
          </form>
        )}
      </Form>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isNavigating: customerKycLoadingUiSelector(state),
  isSubmitting: customerKycSubmitLoadingSelector(state),
  companyDetails: buyerCheckoutDetailsSelector(state),
  draftCompanyDetails: customerKycDraftCompanyDetailsSelector(state),
  hasCompanyName: Boolean(customerCompanyNameSelector(state)),
  isCompanyInformationVerified: customerKyc3ApprovedSelector(state),
  hasPrevPages: customerKycHasPrevPagesSelector(state),
  industryId: customerSelector(state).industry_id,
});

const mapDispatchToProps = (dispatch) => ({
  loadPrevPage: () => dispatch(kycGotoPrevPageRoutine.trigger()),
  gotoNextPage: (payload) => dispatch(kycGotoNextPageRoutine.trigger(payload)),
  setDraftCompanyDetails: (payload) => dispatch(kycSetDraftCompanyDetails(payload)),
  submitIndividualDetails: (payload) => dispatch(kycSubmitCustomerDetailsRoutine.trigger(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyDetailsForm);
